import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from './api';
import { ListParams, Student } from '../types/classroom';
import { ResponseBody } from '../types/common';
import { RootState, store } from './store';
import { studentTotalCntSet } from '../features/classroomSlice';

const studentAdapter = createEntityAdapter({
  selectId: (data: Student) => data.seq,
});
const initialState = studentAdapter.getInitialState({ totalCnt: 0, addCnt: 0 });

export const studentApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getStudent: builder.query({
      query: ({ page = 0, search = '', size = 30 }: ListParams) => ({
        url: 'library/school/mgmt/info/student',
        params: { page, search, size },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.ids.push(...newItems.ids);
          currentCache.ids = [...new Set(currentCache.ids)];
          currentCache.entities = {
            ...currentCache.entities,
            ...newItems.entities,
          };
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => {
        initialState.totalCnt = res.data.totalCnt;
        initialState.addCnt = res.data.addCnt;
        store.dispatch(studentTotalCntSet(res.data.totalCnt));
        return studentAdapter.setAll(initialState, res.data.studentList);
      },
      providesTags: (result, error, arg) => {
        return ['Student'];
      },
    }),
    toggleGroup: builder.mutation({
      query: ({ seq, groupSeq }) => ({
        url: `library/school/mgmt/info/student/${seq}/group`,
        method: 'POST',
        body: { data: { groupSeq } },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    deleteStudent: builder.mutation({
      query: (seq) => ({
        url: `library/school/mgmt/info/student/${seq}`,
        method: 'DELETE',
      }),
    }),
    getSearchStudent: builder.query({
      query: (search) => ({
        url: `library/school/mgmt/info/student/add-form/search`,
        params: { search },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    validateEmail: builder.mutation({
      query: (emailList) => ({
        url: `library/school/mgmt/info/student/add-form/validate`,
        method: 'POST',
        body: { data: emailList },
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
    addStudent: builder.mutation({
      query: ({ data }) => ({
        url: `library/school/mgmt/info/student/add-form/submit`,
        method: 'POST',
        body: { data: data.list },
      }),
    }),
  }),
});

export const {
  useGetStudentQuery,
  useToggleGroupMutation,
  useDeleteStudentMutation,
  useGetSearchStudentQuery,
  useValidateEmailMutation,
  useAddStudentMutation,
} = studentApiSlice;

export const selectStudentResult = studentApiSlice.endpoints.getStudent.select(
  {}
);

const selectStudentData = createSelector(
  selectStudentResult,
  (studentResult) => studentResult.data
);

export const {
  selectAll: selectAllStudents,
  selectById: selectStudentById,
  selectIds: selectStudentIds,
  selectTotal: selectTotalStudentsNum,
} = studentAdapter.getSelectors(
  (state: RootState) => selectStudentData(state) ?? initialState
);
